/**
 * FLIP deltas for field rows displaced by a reorder (DESIGN_SYSTEM.md "Field
 * drag" entry, Animation table).
 *
 * React relocates a reordered row's DOM position instantly on commit — the
 * browser has no notion of "this element used to be somewhere else" to
 * transition from. Comparing each row's top BEFORE the reorder (`prevTops`)
 * against its top AFTER (`nextTops`) is what lets the caller paint a displaced
 * row at its old spot and transition it to the new one instead of letting the
 * layout cut straight over — First/Last/Invert/Play (Paul Lewis), applied only
 * to the rows an actual swap touched.
 *
 * Pure arithmetic only. Measuring `getBoundingClientRect()` and writing the
 * resulting `transform` are both the caller's job (ActiveVocabCard), the same
 * split `deckFocusPositioning.ts` uses for the same reason: jsdom lays nothing
 * out, so the point under test has to be the arithmetic, not the DOM.
 */
/**
 * The vertical translation a computed `transform` currently applies.
 *
 * Needed because a FLIP slide is, by construction, invisible to the style
 * property that started it: `el.style.transform` is set to `''` the moment the
 * transition begins, so for the whole 150ms the only place the in-flight offset
 * exists is the COMPUTED style, as an interpolated matrix. Anything that needs
 * settled layout geometry while a slide is running has to subtract this — see
 * `settledSpans` in cardFieldReorder.ts and the oscillation it was written for.
 *
 * `matrix(a, b, c, d, tx, ty)` carries ty 6th; `matrix3d` carries it 14th
 * (m42). Anything else — `none`, an empty string, an unparseable value — is no
 * translation, which is also the right answer for jsdom, where computed
 * transforms are not resolved at all.
 */
export function translateYOf(transform: string | null | undefined): number {
  if (!transform || transform === 'none') return 0
  const open = transform.indexOf('(')
  if (open === -1) return 0
  const parts = transform.slice(open + 1, transform.lastIndexOf(')')).split(',')
  const index = transform.startsWith('matrix3d') ? 13 : 5
  const value = Number.parseFloat(parts[index])
  return Number.isFinite(value) ? value : 0
}

export function computeFlipDeltas(
  prevTops: ReadonlyMap<string, number>,
  nextTops: ReadonlyMap<string, number>,
): Map<string, number> {
  const deltas = new Map<string, number>()
  for (const [key, nextTop] of nextTops) {
    const prevTop = prevTops.get(key)
    // A row missing from the BEFORE snapshot was just added (or is being
    // measured for the first time) — there is no old position to animate
    // from, so it is left out rather than guessed at. Deletion is the
    // symmetric case and needs nothing here: a removed key is simply absent
    // from `nextTops`, which this loop never visits.
    if (prevTop === undefined) continue
    const delta = prevTop - nextTop
    if (delta !== 0) deltas.set(key, delta)
  }
  return deltas
}
